import { Link } from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core';
import type { ListRow } from './listFilters';
import {
  clickOnEntry,
  LifecycleOf,
  OwnerOf,
  StarOf,
  Stars,
  TagsOf,
} from './rowParts';

/**
 * The list as cards: the same entries, the same order and the same keys as the
 * table, laid out to be scanned rather than compared. A card holds every column
 * the table has, so nothing is lost on a phone, where the table drops some.
 *
 * MUI v4 only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
    gap: theme.spacing(1.5),
    margin: 0,
    padding: theme.spacing(1.5),
    listStyle: 'none',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    cursor: 'pointer',
    '&:hover': { borderColor: 'var(--ui-primary)' },
  },
  active: {
    borderColor: 'var(--ui-primary)',
    boxShadow: '0 0 0 1px var(--ui-primary)',
    background: 'var(--ui-nav-hover-bg)',
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  type: {
    fontSize: '0.75rem',
    letterSpacing: '0.02em',
    color: 'var(--ui-text-muted)',
  },
  title: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: 'var(--ui-text)',
    overflowWrap: 'anywhere',
  },
  description: {
    margin: 0,
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75, 1.5),
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    marginTop: 'auto',
  },
}));

/** Service, Library, API — the chips' own words. */
const typeLabel = (row: ListRow) => {
  if (row.kind === 'api') return 'API';
  const type = row.category || 'component';
  return type.charAt(0).toLocaleUpperCase('en-US') + type.slice(1);
};

export const ListCards = ({
  rows,
  active,
  pathOf,
  onOpen,
  stars,
}: {
  rows: ListRow[];
  active: number;
  pathOf: (row: ListRow) => string;
  onOpen: (index: number) => void;
  stars: Stars;
}) => {
  const classes = useStyles();
  return (
    <ul className={classes.grid} aria-label="Catalog">
      {rows.map((row, index) => (
        // A click anywhere on the card opens it, as a click on a table row
        // does. The keyboard's way in is not missing: the title is a link, and
        // the list's own ↑ ↓ ↵ reach every card (useListKeyboard).
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <li
          key={row.ref}
          data-list-row={index}
          aria-current={index === active ? 'true' : undefined}
          className={`${classes.card} ${
            index === active ? classes.active : ''
          }`}
          onClick={e => clickOnEntry(e, pathOf(row), () => onOpen(index))}
        >
          <div className={classes.top}>
            <span className={classes.type}>{typeLabel(row)}</span>
            <StarOf row={row} stars={stars} />
          </div>
          <Link to={pathOf(row)} className={classes.title}>
            {row.title}
          </Link>
          {row.description && (
            <p className={classes.description}>{row.description}</p>
          )}
          <div className={classes.meta}>
            <OwnerOf row={row} />
            {row.system && (
              <EntityRefLink
                entityRef={row.system}
                defaultKind="system"
                disableTooltip
                hideIcon
              />
            )}
            <LifecycleOf row={row} />
          </div>
          <TagsOf row={row} />
        </li>
      ))}
    </ul>
  );
};
